import { FileWithPath } from 'file-selector';
import { useCallback, useMemo, useState } from 'react';
import { useFilePicker } from 'use-file-picker';

import { FileIcon, FolderIcon, NewFolder } from '@/assets/icons';
import { AddNewIcon } from '@/assets/images';
import { ROOT_FOLDER_PATH } from '@/common/constants';
import { ACCEPTED_TYPES } from '@/common/utils';
import { Dropdown } from '@/components/Dropdown';
import { useDataRoom } from '@/contexts/overview/dataroom/utils';
import { useOverview } from '@/pages/overview/common/utils';
import {
  ConfirmationDialog,
  DialogViewState,
} from '@/pages/overview/dataroom/content/common/ConfirmationDialog';
import { ConfirmNewFolderDialog } from '@/pages/overview/dataroom/content/common/ConfirmNewFolderDialog';
import { uploadNewDataRoomFiles } from '@/pages/overview/dataroom/content/common/utils';

interface DataRoomAddNewProps {
  clientMatterNumber: number;
  clientNumber: number;
  clientMatterId: string;
  dataRoomFileNames: string[];
  filesDirectory: string;
  lookupKey: string;
}

export const DataRoomAddNew = ({
  clientMatterNumber,
  clientNumber,
  clientMatterId,
  dataRoomFileNames,
  filesDirectory,
  lookupKey,
}: DataRoomAddNewProps) => {
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<FileWithPath[]>([]);

  const [viewState, setViewState] = useState<DialogViewState>('ready');

  const { isUserCurrentEditor } = useOverview();
  const { foldersByPaths } = useDataRoom();

  const { openFilePicker } = useFilePicker({
    readAs: 'DataURL',
    accept: ACCEPTED_TYPES,
    multiple: true,
    onFilesSuccessfullySelected: async ({ plainFiles }) => {
      setFilesToUpload(plainFiles);
      setViewState('ready');
      setIsUploadDialogOpen(true);
    },
  });

  const closeDialog = useCallback(() => {
    setIsUploadDialogOpen(false);
    setIsFolderDialogOpen(false);
  }, []);

  const reset = useCallback(() => {
    setFilesToUpload([]);
    closeDialog();
  }, [closeDialog]);

  const onUploadSubmit = useCallback(async () => {
    setViewState('submitting');

    try {
      await uploadNewDataRoomFiles(
        filesToUpload,
        clientMatterNumber,
        clientNumber,
        clientMatterId,
        dataRoomFileNames,
        filesDirectory,
        lookupKey,
      );
      reset();
    } catch (e) {
      setFilesToUpload([]);
      setViewState('errored');
    }
  }, [
    clientMatterId,
    clientMatterNumber,
    clientNumber,
    dataRoomFileNames,
    filesDirectory,
    filesToUpload,
    lookupKey,
    reset,
  ]);

  const getNewFolderName = useCallback(
    (baseFolderName: string) => {
      // Get all root-level folders (those directly under /root)
      const rootFolders = Array.from(foldersByPaths.entries())
        .filter(([path]) => path.startsWith(`${ROOT_FOLDER_PATH}/`) && path.split('/').length === 3)
        .map(([, folders]) => folders[0]) // Get the actual folder object
        .filter((folder): folder is NonNullable<typeof folder> => folder !== undefined);

      // Check if a folder with this name already exists
      const existingFolder = rootFolders.find(
        (folder) =>
          (folder.displayName || folder.name || folder.key).toLowerCase() ===
          baseFolderName.toLowerCase(),
      );

      // If no folder with this name exists, return the original name
      if (!existingFolder) {
        return baseFolderName;
      }

      // Find the highest existing number suffix
      let maxSuffix = 0;
      // Escape special characters in the folder name for regex
      const escapedBaseName = baseFolderName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const baseNameRegex = new RegExp(`^${escapedBaseName}(?:-\\d+)?$`, 'i'); // Case insensitive

      rootFolders.forEach((folder) => {
        const folderName = folder.displayName || folder.name || folder.key;
        if (baseNameRegex.test(folderName)) {
          const match = folderName.match(/-(\d+)$/);
          const suffix = match ? parseInt(match[1]) : 0;
          maxSuffix = Math.max(maxSuffix, suffix);
        }
      });

      // Return the next available name
      return maxSuffix === 0 ? `${baseFolderName}-1` : `${baseFolderName}-${maxSuffix + 1}`;
    },
    [foldersByPaths],
  );

  const options = useMemo(() => {
    return [
      {
        title: 'File upload',
        icon: FileIcon,
        onClick: openFilePicker,
        disabled: !isUserCurrentEditor,
      },
      {
        title: 'Folder upload',
        icon: FolderIcon,
        folderInput: true,
        disabled: !isUserCurrentEditor,
        onChange: async (event: React.ChangeEvent<HTMLInputElement>) => {
          const files = Array.from(event?.target?.files || []);
          if (files.length === 0) return;

          // Get the base folder name from the first file's path
          const firstFilePath = files[0].webkitRelativePath || files[0].name;
          const baseFolderName = firstFilePath.split('/')[0]; // Changed back to [0] since we want the actual folder name
          const newFolderName = getNewFolderName(baseFolderName);

          // Create new File objects with updated paths
          const updatedFiles = files
            .filter((file) => ACCEPTED_TYPES.includes(file.type))
            .map((file) => {
              const relativePath = file.webkitRelativePath || file.name;
              const pathParts = relativePath.split('/');
              // Replace the root folder name with the new folder name
              pathParts[0] = newFolderName;
              const newPath = '/' + pathParts.join('/');
              return new File([file], newPath, { type: file.type });
            });

          setFilesToUpload(updatedFiles);
          setViewState('ready');
          setIsUploadDialogOpen(true);
        },
      },
      {
        title: 'New folder',
        icon: NewFolder,
        onClick: () => setIsFolderDialogOpen(true),
        disabled: !isUserCurrentEditor,
      },
    ];
  }, [isUserCurrentEditor, openFilePicker, getNewFolderName]);

  return (
    <div>
      <Dropdown
        title="New"
        icon={AddNewIcon}
        options={options}
        testId="add-new-button"
        isButtonDisabled={false}
        resizeAction={true}
      />
      {isUploadDialogOpen && (
        <ConfirmationDialog
          title={'Upload Documents?'}
          submitButtonText={'Submit'}
          isOpen={isUploadDialogOpen}
          closeDialog={closeDialog}
          onSubmit={onUploadSubmit}
          onCancel={reset}
          viewState={viewState}
        />
      )}
      {isFolderDialogOpen && (
        <ConfirmNewFolderDialog isDialogOpen={isFolderDialogOpen} closeDialog={closeDialog} />
      )}
    </div>
  );
};
